<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { TechnicalPassportDimensions } from '../types'
import { sockSizeDrawing } from '../sockTypeImages'
import {
  MEASUREMENT_EMPTY_COLOR,
  MEASUREMENT_PLACEMENTS,
  MEASUREMENT_TONE_COLORS,
  formatMeasurementCm,
  sockDrawingKey,
  type MeasurementKey,
  type MeasurementPlacement,
} from '../sockMeasurementTemplate'

const props = defineProps<{
  dimensions: TechnicalPassportDimensions
  sockType?: string
}>()

const { t } = useI18n()

const drawingKey = computed(() => sockDrawingKey(props.sockType))
const image = computed(() => sockSizeDrawing(drawingKey.value))
const labels = computed<Record<MeasurementKey, string>>(() => ({
  cuffHeight: t('engineering.passportStructure.dimensionField.cuff_height_mm.label'),
  cuffWidth: t('engineering.passportStructure.dimensionField.cuff_width_mm.label'),
  overallHeight: t('engineering.passportStructure.dimensionField.leg_height_mm.label'),
  footWidth: t('engineering.passportStructure.dimensionField.foot_width_mm.label'),
  footLength: t('engineering.passportStructure.dimensionField.foot_length_mm.label'),
}))
const values = computed<Record<MeasurementKey, string | null>>(() => ({
  cuffHeight: formatMeasurementCm(props.dimensions.cuff_height_mm),
  cuffWidth: formatMeasurementCm(props.dimensions.cuff_width_mm),
  overallHeight: formatMeasurementCm(props.dimensions.leg_height_mm),
  footWidth: formatMeasurementCm(props.dimensions.foot_width_mm),
  footLength: formatMeasurementCm(props.dimensions.foot_length_mm),
}))

/** Codes are always drawn on their part; the value is appended once it is filled in. */
const activePlacements = computed(() => MEASUREMENT_PLACEMENTS[drawingKey.value])
const letters: Record<MeasurementKey, string> = { overallHeight: 'A', footLength: 'C', cuffWidth: 'D', footWidth: 'E', cuffHeight: 'G' }

function toneColor(placement: MeasurementPlacement): string {
  return values.value[placement.key]
    ? MEASUREMENT_TONE_COLORS[placement.tone]
    : MEASUREMENT_EMPTY_COLOR
}
</script>

<template>
  <figure class="measurement-drawing">
    <figcaption class="measurement-drawing__caption">
      <strong>{{ $t('engineering.passportStructure.measurementTemplate') }}</strong>
      <span>{{ $t('engineering.passportStructure.measurementUnit') }}</span>
    </figcaption>
    <div class="measurement-drawing__canvas">
      <img class="measurement-drawing__image" :src="image" alt="">
      <svg
        class="measurement-drawing__overlay"
        viewBox="0 0 1024 1024"
        role="img"
        :aria-label="$t('engineering.passportStructure.drawingAria')"
      >
        <text
          v-for="placement in activePlacements"
          :key="placement.key"
          class="measurement-drawing__value"
          :class="[`is-${placement.tone}`, { 'is-empty': !values[placement.key] }]"
          :style="{ fill: toneColor(placement) }"
          :x="placement.x"
          :y="placement.y"
          :text-anchor="placement.anchor ?? 'middle'"
        >
          <title>{{ letters[placement.key] }} · {{ labels[placement.key] }}: {{ values[placement.key] ? `${values[placement.key]} cm` : '—' }}</title>
          {{ values[placement.key] ? `${letters[placement.key]} ${values[placement.key]}` : letters[placement.key] }}
        </text>
      </svg>
    </div>
  </figure>
</template>

<style scoped>
.measurement-drawing {
  display: grid;
  gap: 8px;
  margin: 0 0 14px;
  padding: 12px;
  border: 1px solid #c6d5e5;
  background: linear-gradient(145deg, #f5f9fd 0%, #eaf2fa 100%);
}

.measurement-drawing__caption {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.measurement-drawing__caption strong {
  color: #173454;
  font-size: 13px;
}

.measurement-drawing__caption span {
  color: #66778b;
  font-size: 11px;
  font-weight: 600;
}

.measurement-drawing__canvas {
  position: relative;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
}

.measurement-drawing__image {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid #cbd8e5;
  background: #fff;
}

.measurement-drawing__overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.measurement-drawing__value {
  font-size: 30px;
  font-weight: 800;
  paint-order: stroke;
  stroke: #fff;
  stroke-width: 8px;
  stroke-linejoin: round;
}

.measurement-drawing__value.is-red {
  fill: #c0392b;
}

.measurement-drawing__value.is-green {
  fill: #2e8b57;
}

.measurement-drawing__value.is-teal {
  fill: #0f8b8d;
}

.measurement-drawing__value.is-purple {
  fill: #7b4fa0;
}

.measurement-drawing__value.is-blue {
  fill: #2f6fb5;
}

.measurement-drawing__value.is-empty {
  fill: #8a9792;
  font-size: 26px;
  stroke-width: 6px;
}
</style>
